import deepExtend from "deep-extend";
import { List } from "immutable";
import { createAction } from "redux-actions";
import * as AlertActions from "./NotificationActions";
import { Thunk } from "./Thunk";
import * as M from "../models";
import { Api } from "../services/Api";
import _ from "lodash";

const prefix = "ADMIN_EVENT_";

export class Constants {
    public static EVENT_SUBSCRIBED = prefix + "EVENT_SUBSCRIBED";
    public static LOADING_EVENT = prefix + "LOADING_EVENT";
    public static LOADING_LIST_EVENTS = prefix + "LOADING_LIST_EVENTS";
    public static LOADING_SUB_EVENTS = prefix + "LOADING_SUB_EVENTS";
    public static UPDATED_EVENTS = prefix + "UPDATED_EVENTS";
    public static UPDATED_LIST_EVENTS = prefix + "UPDATED_LIST_EVENTS";
    public static UPDATED_REMOTELY = prefix + "UPDATED_REMOTELY";
    public static UPDATED_SUB_EVENTS = prefix + "UPDATED_SUB_EVENTS";
}

interface LoadingEventPayload {
    eventId: number | string;
    loading: boolean;
}

interface SubscribedPayload {
    id: number;
    subscribed: boolean;
}

export const loadListEvents = (): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const result = await api.get(Api.endpoints.admin.event.mainEvents);

    if (result.isSuccess && result.data)
        dispatch(updateListEvents(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingListEvents(false));
};

export const loadEvent = (eventId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const result = await api.get(Api.endpoints.admin.event.event.withUrlParams({ eventId }));

    if (result.isSuccess && result.data)
        dispatch(updateEvent(result.data));
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingEvent(eventId, false));
};

export const loadSubEvents = (eventId: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const api = new Api(state);
    const result = await api.get(Api.endpoints.admin.event.subEvents.withUrlParams({ eventId }));

    if (result.isSuccess && result.data) {
        dispatch(replaceParentSubEvents(result.data, eventId));
    }
    else
        dispatch(AlertActions.notifyError(M.Result.Result.errorsOrMessage(result).join("<br />")));

    dispatch(toggleLoadingSubEvents(false));
};

export const removeEvent = (evt: M.Event.Event | M.Event.EventAdminListItem): Thunk => async (dispatch, getState) => {
    const events = getState().adminEvents.events;
    const idx = events.findIndex((x: M.Event.Event) => x.id === evt.id);
    if (idx > -1)
        dispatch(onEventsUpdated(events.remove(idx)));

    // also remove from abstract list
    const listEvents = getState().adminEvents.listEvents;
    const listIdx = listEvents.findIndex((x: M.Event.EventAdminListItem) => x.id === evt.id);
    if (listIdx > -1)
        dispatch(onListEventsUpdated(listEvents.remove(listIdx)));
};

export const removeSubEvent = (evt: M.Event.SubEvent): Thunk => async (dispatch, getState) => {
    const events = getState().adminEvents.subEvents;
    const idx = events.findIndex((x: M.Event.SubEvent) => x.id === evt.id);
    if (idx > -1)
        dispatch(onSubEventsUpdated(events.remove(idx)));
    else {
        const parentEvt = events.find((x) => x !== undefined && x.id === evt.parentId);
        if (parentEvt) {
            const children = List(parentEvt.children);
            const childIdx = children.findIndex((x) => x !== undefined && x.id === evt.id);
            if (childIdx > -1) {
                parentEvt.children = children.remove(childIdx).toArray();
                dispatch(updateSubEvent(parentEvt));
            }
        }
    }
};

export const replaceParentSubEvents = (updatedList: M.Event.SubEvent[], parentId: number): Thunk => async (dispatch, getState) => {
    let list = getState().adminEvents.subEvents;
    const parent = list.find((item: M.Event.SubEvent) => item.id === parentId);

    if (parent) {
        // parent is a sub event
        parent.children = updatedList;
        dispatch(updateSubEvent(parent));
    }
    else {
        // parent is the main event
        list = list
            .filter((item: M.Event.SubEvent) => item.parentId !== parentId)
            .toList();

        for (const item of updatedList)
            list = list.push(item);

        dispatch(onSubEventsUpdated(list));
    }
};

export const resetEventSubscriptions = (): Thunk => (dispatch, getState) => {
    const eventIds = Object.keys(getState().adminEvents.subscribedEvents);

    eventIds.forEach((id) => {
        console.log("unsubscribing from event", id);
        dispatch(onEventSubscribed(parseInt(id, 10), false));
    });

    eventIds.forEach(async (id) => {
        console.log("re-subscribing to event", id);
        await dispatch(subscribeToEvent(parseInt(id, 10)));
    });
};

export const subscribeToEvent = (eventId: number): Thunk => async (dispatch, getState) => {
    const sub = getState().adminEvents.subscribedEvents[eventId];
    if (sub !== undefined)
        return;

    const connection = getState().dbSubscriptions.websocket;
    if (!connection)
        return;

    try {
        connection.on("EventUpdated", (id) => {
            if (id === eventId) {
                console.log(`received updates. Will refetch event ${id} shortly...`);
                dispatch(onEventUpdatedRemotely(id));
            }
        });

        connection.on("EventSubscribed", (id) => {
            if (id === eventId) {
                console.log("subscribed to event updates for ID", id);
                dispatch(onEventSubscribed(id, true));
            }
        });

        await connection.invoke("EventSubscribe", eventId);
    }
    catch (err) {
        console.warn("Subscription error", eventId, err);
    }
};

export const updateEvent = (item: M.Event.Event): Thunk => async (dispatch, getState) => {
    dispatch(updateEvents([item]));
};

export const updateEvents = (updatedList: M.Event.Event[]): Thunk => async (dispatch, getState) => {
    let list = getState().adminEvents.events;
    updatedList.map((item: M.Event.Event) => {
        const idx = list.findIndex((x: M.Event.Event) => x.id === item.id);
        list = idx > -1
            ? list.set(idx, item)
            : list.push(item);
    });

    dispatch(onEventsUpdated(list));
};

export const updateListEvent = (a: M.Event.EventAdminListItem): Thunk => async (dispatch, getState) => {
    dispatch(updateListEvents([a]));
};

export const updateListEvents = (updatedList: M.Event.EventAdminListItem[]): Thunk => async (dispatch, getState) => {
    let list = getState().adminEvents.listEvents;
    updatedList.map((item: M.Event.EventAdminListItem) => {
        const idx = list.findIndex((x: M.Event.EventAdminListItem) => x.id === item.id);
        list = idx > -1
            ? list.set(idx, item)
            : list.push(item);
    });

    dispatch(onListEventsUpdated(list));
};

export const updateSubEvent = (item: M.Event.SubEvent): Thunk => async (dispatch, getState) => {
    dispatch(updateSubEvents([item]));
};

export const updateSubEvents = (updatedList: M.Event.SubEvent[]): Thunk => async (dispatch, getState) => {
    let list = getState().adminEvents.subEvents;
    updatedList.map((item: M.Event.SubEvent) => {
        const idx = list.findIndex((x: M.Event.SubEvent) => x.id === item.id);
        list = idx > -1
            ? list.set(idx, item)
            : list.push(item);
    });

    dispatch(onSubEventsUpdated(list));
};

export const updateSubEventSeatsLeft = (eventId: number, seatsLeft: number): Thunk => async (dispatch, getState) => {
    const state = getState();
    const _evt = state.adminEvents.subEvents.find((x) => x !== undefined && x.id === eventId);

    if (_evt) {
        const evt = deepExtend({}, _evt);
        evt.seatsLeft = seatsLeft;
        dispatch(updateSubEvent(evt));
        return;
    }

    // if sub event was not found, search sessions of sub events
    for (const subEvt of state.adminEvents.subEvents.toArray()) {
        const idx = _.findIndex(subEvt.children, (x) => x.id === eventId);
        if (idx > -1) {
            const evt = deepExtend({}, subEvt);
            evt.children[idx].seatsLeft = seatsLeft;
            dispatch(updateSubEvent(evt));
            return;
        }
    }
};

export const onEventSubscribed = createAction<SubscribedPayload, number, boolean>(
    Constants.EVENT_SUBSCRIBED, (id, subscribed) => ({ id, subscribed }),
);

export const onEventUpdatedRemotely = createAction<number, number>(
    Constants.UPDATED_REMOTELY, (eventId) => (eventId),
);

export const onEventsUpdated = createAction<List<M.Event.Event>, List<M.Event.Event>>(
    Constants.UPDATED_EVENTS, (events) => (events),
);

export const onListEventsUpdated = createAction<List<M.Event.EventAdminListItem>, List<M.Event.EventAdminListItem>>(
    Constants.UPDATED_LIST_EVENTS, (events) => (events),
);

export const onSubEventsUpdated = createAction<List<M.Event.SubEvent>, List<M.Event.SubEvent>>(
    Constants.UPDATED_SUB_EVENTS, (events) => (events),
);

export const toggleLoadingEvent = createAction<LoadingEventPayload, number | string, boolean>(
    Constants.LOADING_EVENT, (eventId, loading) => ({ eventId, loading }),
);

export const toggleLoadingListEvents = createAction<boolean, boolean>(
    Constants.LOADING_LIST_EVENTS, (loading) => (loading),
);

export const toggleLoadingSubEvents = createAction<boolean, boolean>(
    Constants.LOADING_SUB_EVENTS, (loading) => (loading),
);



// WEBPACK FOOTER //
// ./src/actions/AdminEventActions.ts